import { useEffect, useReducer, useState } from 'react';
import { ChevronDown, MessageCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { AuthExpired, NetworkError, ParseError, SourceError } from '@/lib/errors';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ComicSource, SourceAuth, SourceComment } from '@/sources/types';

import {
  canLoadMoreComments,
  commentPaginationReducer,
  flattenComments,
  initialCommentPaginationState,
  nextCommentPage,
} from './commentPagination';

interface SourceCommentsProps {
  source: ComicSource;
  comicId: string;
  auth?: SourceAuth;
  pageSize?: number;
}

export function SourceComments({ source, comicId, auth, pageSize = 20 }: SourceCommentsProps) {
  const supportsComments = source.capabilities.comments === true && typeof source.getComments === 'function';
  const [expanded, setExpanded] = useState(false);
  const [state, dispatch] = useReducer(commentPaginationReducer, undefined, initialCommentPaginationState);

  useEffect(() => {
    setExpanded(false);
    dispatch({ type: 'reset' });
  }, [source.id, comicId, auth]);

  useEffect(() => {
    if (!supportsComments || !expanded || state.loading || state.error !== null || state.pages.length > 0) {
      return;
    }
    dispatch({ type: 'request', page: 1 });
  }, [expanded, state.error, state.loading, state.pages.length, supportsComments]);

  useEffect(() => {
    if (!supportsComments || state.inFlightPage === null || !source.getComments) return;
    let cancelled = false;
    const page = state.inFlightPage;
    source
      .getComments(comicId, auth, { page, pageSize })
      .then((commentPage) => {
        if (!cancelled) dispatch({ type: 'success', page: commentPage });
      })
      .catch((err: unknown) => {
        if (!cancelled) dispatch({ type: 'failure', error: err, page });
      });
    return () => {
      cancelled = true;
    };
  }, [auth, comicId, pageSize, source, state.inFlightPage, supportsComments]);

  if (!supportsComments) return null;

  const comments = flattenComments(state);
  const firstPage = state.pages[0];
  const latestPage = state.pages[state.pages.length - 1];
  const totalItems = latestPage?.totalItems ?? firstPage?.totalItems;
  const totalPages = latestPage?.totalPages ?? firstPage?.totalPages;
  const loadedCount = comments.length;
  const status = formatStatus({
    expanded,
    loadedCount,
    loading: state.loading,
    totalItems,
    totalPages,
    page: latestPage?.page,
  });
  const hasLoadMore = expanded && canLoadMoreComments(state) && state.pages.length > 0;
  const errorMessage = state.error === null ? null : formatCommentError(state.error);

  function toggleExpanded() {
    setExpanded((current) => !current);
  }

  function loadMore() {
    if (!canLoadMoreComments(state)) return;
    dispatch({ type: 'request', page: nextCommentPage(state) });
  }

  function retry() {
    dispatch({ type: 'retry' });
    dispatch({ type: 'request', page: nextCommentPage(state) });
  }

  return (
    <section className="flex flex-col gap-3" aria-labelledby="source-comments-heading">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-border/60 bg-card p-3 text-left transition hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-expanded={expanded}
        aria-controls="source-comments-content"
        onClick={toggleExpanded}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="rounded-full bg-muted p-2 text-muted-foreground">
            <MessageCircle className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span id="source-comments-heading" className="block text-lg font-semibold">
              Comments
            </span>
            <span className="block truncate text-sm text-muted-foreground">{status}</span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {!expanded && (
            <span className="hidden text-sm font-medium text-primary sm:inline">View comments</span>
          )}
          <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
        </span>
      </button>

      {expanded && (
        <div id="source-comments-content" className="flex flex-col gap-3">
          {state.loading && comments.length === 0 && <CommentSkeletons />}
          {comments.length > 0 && (
            <div className="flex flex-col gap-2">
              {comments.map((comment) => (
                <CommentCard key={comment.id} comment={comment} />
              ))}
            </div>
          )}
          {!state.loading && !state.error && state.pages.length > 0 && comments.length === 0 && (
            <p className="rounded-lg border border-border/60 p-3 text-sm text-muted-foreground">
              No comments yet.
            </p>
          )}
          {errorMessage && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-sm text-destructive" role="alert">
                Could not load comments: {errorMessage}
              </p>
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={retry}>
                Retry
              </Button>
            </div>
          )}
          {hasLoadMore && (
            <div>
              <Button type="button" variant="outline" onClick={loadMore} disabled={state.loading}>
                {state.loading ? 'Loading comments...' : 'Load more comments'}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function formatStatus({
  expanded,
  loadedCount,
  loading,
  totalItems,
  totalPages,
  page,
}: {
  expanded: boolean;
  loadedCount: number;
  loading: boolean;
  totalItems?: number;
  totalPages?: number;
  page?: number;
}): string {
  if (!expanded) return totalItems ? `${totalItems} available` : 'Collapsed';
  if (loading && loadedCount === 0) return 'Loading...';
  if (totalItems !== undefined) return `Showing ${loadedCount} of ${totalItems}`;
  if (page !== undefined && totalPages !== undefined) return `Page ${page} of ${totalPages}`;
  return loadedCount > 0 ? `${loadedCount} loaded` : 'Ready';
}

function formatCommentError(error: unknown): string {
  if (error instanceof AuthExpired) {
    return 'Your session for this source has expired. Reconnect the account and try again.';
  }
  if (error instanceof NetworkError) {
    return error.status !== undefined
      ? `Request failed with status ${error.status}. The source may be temporarily unavailable.`
      : 'Failed to reach the source through the proxy. Check your connection and proxy settings.';
  }
  if (error instanceof ParseError) {
    return 'Could not read comments from this source. Try again, or report this if it keeps happening.';
  }
  if (error instanceof SourceError) {
    return error.message || 'Unknown source-level comments error.';
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

function CommentSkeletons() {
  return (
    <div className="flex flex-col gap-2" aria-label="Loading comments">
      {[0, 1].map((index) => (
        <div key={index} className="rounded-lg border border-border/60 p-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function CommentCard({ comment }: { comment: SourceComment }) {
  return (
    <article className="rounded-lg border border-border/60 bg-card p-3">
      <div className="flex items-start gap-3">
        {comment.authorAvatarUrl && (
          <img
            src={comment.authorAvatarUrl}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            className="h-9 w-9 shrink-0 rounded-full object-cover"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{comment.authorName}</span>
            {comment.createdAt !== undefined && <time>{formatCommentDate(comment.createdAt)}</time>}
            {comment.score !== undefined && <span>{formatScore(comment.score)}</span>}
          </p>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6">{comment.body}</p>
          {comment.replyCount !== undefined && comment.replyCount > 0 && (
            <button
              type="button"
              className="mt-2 text-xs font-medium text-muted-foreground"
              disabled
              title="Threaded comment loading is not available yet"
            >
              View {comment.replyCount} {comment.replyCount === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function formatCommentDate(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp));
}

function formatScore(score: number): string {
  if (score === 0) return '0 points';
  return `${score > 0 ? '+' : ''}${score} ${Math.abs(score) === 1 ? 'point' : 'points'}`;
}
